import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * Fades a horizontally-scrollable row's right edge only while there is more to scroll — not when the
 * items already fit, and not once scrolled to the end. `itemCount` re-syncs the listeners when the set
 * changes. Returns a `ref` for the scroll container and a `showFade` flag to gate the fade mask.
 */
export function useCarouselScrollFade(itemCount: number): {
	ref: RefObject<HTMLDivElement | null>;
	showFade: boolean;
} {
	const ref = useRef<HTMLDivElement>(null);
	const [showFade, setShowFade] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		const update = () => {
			setShowFade(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
		};
		update();
		el.addEventListener('scroll', update, { passive: true });
		window.addEventListener('resize', update);
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
		observer?.observe(el);
		return () => {
			el.removeEventListener('scroll', update);
			window.removeEventListener('resize', update);
			observer?.disconnect();
		};
	}, [itemCount]);

	return { ref, showFade };
}
